import React, { useCallback, useState } from 'react';
import {
  Box,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AuthUtil from '../../../redux/AuthUtil';
import { TICKET_OUT } from '../../global/Server/endpoints';
import ZoomablePhoto from './ZoomablePhoto';

// Shared full-screen viewer for a Ticket Out photo (spec §9). Photos are
// fetched on demand from /ticketOut/previewPhoto by ticketId rather than
// shipped inline with the session report, keeping the report payload small.
//
// The viewer uses the same fullscreen ZoomablePhoto lightbox as the audit
// modes (pinch/double-tap/wheel zoom, pan) so the picture can be inspected
// closely and — inside the Flutter WebView wrapper — single-finger pans on
// the photo no longer trigger pull-to-refresh (ZoomablePhoto sets
// touch-action: none).
//
// Returns { view(id), node } — render `node` once in the component and call
// `view(id)` to open the viewer. The screen is already gated to
// managers/owners, and previewPhoto is @Secured, so no extra role check here.
//
// Defaults to the Ticket Out photo endpoint, but the same viewer is reused for
// Transaction photos ("EXTRA MATCH / LUCKY") by passing a different endpoint,
// id key, and title.
export default function useTicketPhotoViewer({
  endpoint = TICKET_OUT.PREVIEW_PHOTO,
  idKey = 'ticketId',
  title = 'Ticket Photo',
} = {}) {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  const view = useCallback(
    (id) => {
      if (!id) return;
      setOpen(true);
      setLoading(true);
      setSrc(null);
      AuthUtil.postRequest(endpoint, { [idKey]: id })
      .then((res) => {
        if (res.data && res.data.success && res.data.previewedFile) {
          const blob = new Blob([new Uint8Array(res.data.previewedFile)], {
            type: 'image/jpeg',
          });
          setSrc(URL.createObjectURL(blob));
        }
      })
      .catch(() => {
        /* leave src null → "Photo not available" */
      })
      .finally(() => setLoading(false));
    },
    [endpoint, idKey]
  );

  const close = useCallback(() => {
    setOpen(false);
    setSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const node = (
    <Dialog
      open={open}
      onClose={close}
      fullScreen
      PaperProps={{ sx: { background: '#000' } }}
    >
      <AppBar
        position="fixed"
        sx={{ background: 'rgba(0,0,0,0.6)', boxShadow: 0, zIndex: 1 }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          <Typography sx={{ flex: 1, fontSize: 13, color: '#fff' }}>
            {title}
          </Typography>
          <IconButton onClick={close}>
            <CloseIcon sx={{ color: '#fff' }} />
          </IconButton>
        </Toolbar>
      </AppBar>
      {src ? (
        <ZoomablePhoto src={src} alt="Ticket" />
      ) : (
        <Box
          sx={{
            position: 'absolute',
            top: '56px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.7)',
            fontSize: 14,
          }}
        >
          {loading ? 'Loading…' : 'Photo not available.'}
        </Box>
      )}
    </Dialog>
  );

  return { view, node };
}
